import {
  isNotFoundError,
  useMe,
  useProject,
  useTicket,
  useTicketWatchers,
} from '@servicetim/ui/api';
import {
  buttonVariants,
  ConflictDialog,
  EmptyState,
  LoadingState,
  ProblemBanner,
} from '@servicetim/ui/components';
import { useSessionClaims } from '@servicetim/ui/lib';
import { Link } from '@tanstack/react-router';
import { SearchX } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { ActivityTabs } from './activity';
import { useAgentUserOptionLoader, useProjectStatuses, useUserDirectory } from './api';
import { ApprovalsCard } from './approvals-card';
import { AttachmentsCard } from './attachments/attachments-card';
import { CloneDialog } from './clone-dialog';
import { DatesCard } from './dates-card';
import { LabelsCard } from './labels-card';
import { LinksCard } from './links-card';
import { MoveDialog } from './move-dialog';
import { PeopleCard } from './people-card';
import { SlaCard } from './sla-card';
import { SubTicketsCard } from './sub-tickets-card';
import { type TicketContextValue, TicketProvider } from './ticket-context';
import { TicketDescription } from './ticket-description';
import { TicketFields } from './ticket-fields';
import { TicketHeader } from './ticket-header';
import { TransitionsBar } from './transitions-bar';
import { useTicketPatch } from './use-ticket-patch';
import { WatchersCard } from './watchers-card';

/** `/tickets/$idOrKey`: loads the ticket, builds the shared context and lays out the sections. */
export function TicketDetailScreen({ idOrKey }: { idOrKey: string }) {
  const ticketQuery = useTicket(idOrKey);
  const ticket = ticketQuery.data;
  const project = useProject(ticket?.projectId);
  const statuses = useProjectStatuses(ticket?.projectId);
  const watchers = useTicketWatchers(ticket?.id);
  const directory = useUserDirectory();
  const me = useMe();
  const { userId: meId } = useSessionClaims();
  const loadUsers = useAgentUserOptionLoader();
  const { patch, isPending: patching, dialogProps } = useTicketPatch(ticket, ticketQuery.refetch);
  const [moveOpen, setMoveOpen] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);

  const userLabel = useCallback(
    (id: string | null | undefined): string | null => {
      if (!id || !ticket) return null;
      if (id === meId && me.data) return me.data.user.displayName;
      const d = ticket.display;
      if (id === ticket.reporterId && d.reporter_id) return d.reporter_id;
      if (id === ticket.assigneeId && d.assignee_id) return d.assignee_id;
      if (id === ticket.acknowledgedBy && d.acknowledged_by) return d.acknowledged_by;
      if (id === ticket.createdBy && d.created_by) return d.created_by;
      const watcher = watchers.data?.find((w) => w.userId === id);
      if (watcher) return watcher.displayName;
      return directory.data?.get(id) ?? null;
    },
    [ticket, meId, me.data, watchers.data, directory.data],
  );

  const value = useMemo<TicketContextValue | null>(() => {
    if (!ticket) return null;
    return {
      ticket,
      idOrKey,
      project: project.data,
      canAdmin: project.data?.canAdmin ?? false,
      statuses,
      status: statuses.byId.get(ticket.statusId),
      meId,
      refetch: ticketQuery.refetch,
      patch,
      patching,
      userLabel,
      loadUsers,
    };
  }, [
    ticket,
    idOrKey,
    project.data,
    statuses,
    meId,
    ticketQuery.refetch,
    patch,
    patching,
    userLabel,
    loadUsers,
  ]);

  if (ticketQuery.isPending) return <LoadingState rows={6} label="Loading the ticket…" />;
  if (ticketQuery.error || !value) {
    if (isNotFoundError(ticketQuery.error)) {
      return (
        <EmptyState
          icon={SearchX}
          title={`No ticket ${idOrKey}`}
          description="It does not exist, was deleted, or you cannot see it."
          action={
            <Link to="/queue" search={{}} className={buttonVariants({ variant: 'outline' })}>
              Back to the queue
            </Link>
          }
        />
      );
    }
    return <ProblemBanner error={ticketQuery.error} onRetry={() => void ticketQuery.refetch()} />;
  }

  return (
    <TicketProvider value={value}>
      <div className="mx-auto w-full max-w-7xl space-y-6" data-ticket-id={value.ticket.id}>
        <TicketHeader onMove={() => setMoveOpen(true)} onClone={() => setCloneOpen(true)} />
        <TransitionsBar />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <div className="min-w-0 space-y-6">
            <TicketDescription />
            <TicketFields />
            <ActivityTabs />
          </div>
          <aside className="space-y-4" aria-label="Ticket details">
            <PeopleCard />
            <DatesCard />
            <ApprovalsCard />
            <SlaCard />
            <LabelsCard />
            <WatchersCard />
            <LinksCard />
            <AttachmentsCard />
            <SubTicketsCard />
          </aside>
        </div>
        <ConflictDialog {...dialogProps} subject={`ticket ${value.ticket.key}`} />
        <MoveDialog open={moveOpen} onOpenChange={setMoveOpen} />
        <CloneDialog open={cloneOpen} onOpenChange={setCloneOpen} />
      </div>
    </TicketProvider>
  );
}
